<template>
  <form @submit.prevent="handleSubmit" novalidate style="position: relative">
    <b-loading v-model="loading" :is-full-page="false"></b-loading>

    <b-notification v-if="form.errors.has('NOFIELD')" type="is-danger" role="alert">
      {{ form.errors.get('NOFIELD') }}
    </b-notification>

    <div class="block">
      <div>
        <AjaxSelectField
          label="Select a provider"
          placeholder="e.g. Healthcare Facility"
          :auto-select="true"
          :options="activeProviderOptions"
          :errors="form.errors.get('provider_id')"
          v-model="form.provider_id"
        />
        <AjaxSelectField
          :loading="payerOptions.length"
          label="Select a payer"
          placeholder="e.g. Blue Cross"
          :options="payerOptions"
          :value="form.payer_id"
          :errors="form.errors.get('payer_id')"
          @input="handlePayerSelected"
        />
        <b-notification
          v-if="selectedPayerNote"
          type="is-info"
          :closable="false"
          role="note"
          class="mt-2"
        >
          {{ selectedPayerNote }}
        </b-notification>
      </div>
    </div>

    <hr />

    <div v-if="schema">
      <div class="columns" v-if="comboOptions.length > 1">
        <div class="column">
          <b-field label="What patient information do you have?" :message="comboHelp">
            <b-select expanded v-model="form.selectedCombo">
              <option v-for="option in comboOptions" :value="option.index" :key="option.index">
                {{ option.label }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div class="columns">
        <div class="column" v-if="combos[form.selectedCombo].includes('groupNumber')">
          <TextField
            v-model="form.groupNumber"
            :schema="schema.groupNumber"
            :errors="form.errors.get('groupNumber')"
          ></TextField>
        </div>
        <div class="column" v-if="combos[form.selectedCombo].includes('memberId')">
          <TextField
            v-model="form.memberId"
            :schema="schema.memberId"
            :errors="form.errors.get('memberId')"
          ></TextField>
        </div>
        <div class="column" v-if="combos[form.selectedCombo].includes('patientBirthDate')">
          <DateField
            v-model="form.patientBirthDate"
            :schema="schema.patientBirthDate"
            :errors="form.errors.get('patientBirthDate')"
          ></DateField>
        </div>
      </div>

      <div class="columns">
        <div class="column" v-if="combos[form.selectedCombo].includes('patientFirstName')">
          <TextField
            v-model="form.patientFirstName"
            :schema="schema.patientFirstName"
            :errors="form.errors.get('patientFirstName')"
          ></TextField>
        </div>
        <div class="column" v-if="combos[form.selectedCombo].includes('patientLastName')">
          <TextField
            v-model="form.patientLastName"
            :schema="schema.patientLastName"
            :errors="form.errors.get('patientLastName')"
          ></TextField>
        </div>
        <div class="column" v-if="combos[form.selectedCombo].includes('patientSSN') && schema.patientSSN">
          <TextField
            v-model="form.patientSSN"
            :schema="schema.patientSSN"
            :errors="form.errors.get('patientSSN')"
          ></TextField>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <MultiCheckboxField
            v-if="schema.serviceType.multiple"
            v-model="serviceType"
            :schema="schema.serviceType"
            :errors="form.errors.get('serviceType')"
          ></MultiCheckboxField>
          <RadioField
            v-if="!schema.serviceType.multiple"
            v-model="form.serviceType"
            :schema="schema.serviceType"
            :errors="form.errors.get('serviceType')"
          ></RadioField>
        </div>
        <div class="column">
          <div class="block" v-if="schema.patientGender?.allowed">
            <RadioField
              v-model="form.patientGender"
              :schema="schema.patientGender"
              :errors="form.errors.get('patientGender')"
            ></RadioField>
          </div>
          <div class="block" v-if="schema.subscriberRelationship?.allowed">
            <RadioField
              v-model="form.subscriberRelationship"
              :schema="schema.subscriberRelationship"
              :errors="form.errors.get('subscriberRelationship')"
            ></RadioField>
          </div>
        </div>
      </div>
      <hr />
      <div class="form-footer">
        <div class="footer-row">
          <div class="as-of-today-wrapper">
            <div class="as-of-today-btn" :class="{ 'is-active': useToday }">
              <b-switch
                v-model="useToday"
                :type="form.errors.has('asOfDate') ? 'is-danger' : 'is-primary'"
                @input="$event === true && delete form['asOfDate']"
              ></b-switch>
              <span class="as-of-today-text">As Of Today</span>
            </div>
          </div>
          <div class="datepicker-wrapper" v-if="schema.asOfDate?.allowed && !useToday">
            <b-field
              :label="schema.asOfDate.label"
              :message="form.errors.get('asOfDate')"
              :type="{ 'is-danger': form.errors.has('asOfDate') }"
            >
              <b-datepicker
                v-model="form.asOfDate"
                :min-date="new Date(schema.asOfDate.min)"
                :max-date="new Date()"
                position="is-top-right"
                locale="en-US"
                placeholder="Click to select..."
                icon="calendar-today"
                :use-html5-validation="false"
                trap-focus
                :date-parser="dateParser"
              ></b-datepicker>
            </b-field>
          </div>
        </div>
        <div class="submit-buttons-row">
          <div class="buttons submit-buttons">
            <b-button
              @click="handleSubmit('draft')"
              :loading="loading"
              type="is-gray"
              size="is-medium"
              native-type="submit"
              class="save-draft-btn submit-btn"
            >
              Save As Draft
            </b-button>
            <b-button
              @click="handleSubmit('submit')"
              :loading="loading"
              type="is-primary"
              size="is-medium"
              native-type="submit"
              class="submit-btn"
            >
              Submit Inquiry
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import request from '../request';
import Form from '../utils/form';
import { validate, required, requiredAny } from '../utils/validate';
import { dateParser } from '../utils/dateParser';
import AjaxSelectField from '../components/fields/AjaxSelectField.vue';
import DateField from '../components/fields/DateField.vue';
import MultiCheckboxField from '../components/fields/MultiCheckboxField.vue';
import RadioField from '../components/fields/RadioField.vue';
import TextField from '../components/fields/TextField.vue';
const defaultFormFields = {
  // Internal
  provider_id: null,
  payer_id: null,

  // Always used
  memberId: null,
  patientBirthDate: null,

  // Frequently used
  patientFirstName: null,
  patientLastName: null,
  patientGender: null,
  asOfDate: null,
  serviceType: null,
  subscriberRelationship: null,

  // Infrequently used
  groupNumber: null,
  patientSuffix: null,
  patientSSN: null,
  submitterId: null,
  parentId: null,
  draftId: null,

  // Field combination,
  selectedCombo: 0,
};

// shortFieldLabels drops the redundant "Patient" prefix from labels where the form
// context already makes the owner obvious. Patient ID keeps its prefix on purpose:
// it is the field where "Patient" disambiguates it from the provider identifiers.
const shortFieldLabels = {
  patientFirstName: 'First Name',
  patientLastName: 'Last Name',
};

// applyShortLabels overrides display labels on a payer schema. Field names are left
// alone, so nothing about what the form submits changes.
function applyShortLabels(fields) {
  const patched = { ...fields };
  for (const [name, label] of Object.entries(shortFieldLabels)) {
    if (patched[name]) {
      patched[name] = { ...patched[name], label };
    }
  }
  return patched;
}

const copyableFormFields = [
  'provider_id',
  'payer_id',
  'memberId',
  'patientBirthDate',
  'patientFirstName',
  'patientLastName',
  'selectedCombo',
];

const comboFields = [
  'groupNumber',
  'memberId',
  'patientBirthDate',
  'patientFirstName',
  'patientLastName',
  'patientSSN',
];

// Combos come from Availity and can name fields the server never enforces
// (groupNumber, for one). Only these are checked by validateSchema.
const serverValidatedComboFields = [
  'memberId',
  'patientFirstName',
  'patientLastName',
  'patientSSN',
  'patientBirthDate',
];

// pverify reports failures in the same `errors` map, keyed by its own names.
// See backend/pverify/eligibility.go.
const vendorErrorKeys = ['Message', 'ErrorCode', 'ErrorDescription', 'FollowUpAction'];

const fieldSummaryMessage = 'Please correct the highlighted fields below.';

export default {
  name: 'InquiryForm',
  components: {
    AjaxSelectField,
    DateField,
    MultiCheckboxField,
    RadioField,
    TextField,
  },
  props: {
    // inquiry can be passed in to prepopulate form.
    // copy() method must be called to actually populate.
    inquiry: {
      required: false,
      type: Object,
    },
  },
  data() {
    return {
      loading: true,
      payerOptions: [],
      providerOptions: [],
      form: new Form(defaultFormFields),
      schema: null,
      useToday: true,
      combos: [],
    };
  },
  mounted() {
    Promise.all([
      request({ url: '/payers' }).then((resp) => (this.payerOptions = resp.data.payers)),
      request({ url: '/providers/compact' }).then(
        (resp) => (this.providerOptions = resp.data.providers)
      ),
    ]).finally(() => {
      if (this.inquiry !== undefined) {
        this.populateForm(this.inquiry);
      }
      this.loading = false;
    });
  },
  computed: {
    activeProviderOptions() {
      return this.providerOptions.filter((opt) => opt.is_active);
    },
    // Admin-managed note for the selected payer, kept visible after selection.
    selectedPayerNote() {
      if (!this.form.payer_id) {
        return '';
      }
      const payer = this.payerOptions.find((opt) => opt.id === this.form.payer_id);
      return payer ? payer.note : '';
    },
    referenceRules() {
      return {
        provider_id: required('Please select a valid Provider.'),
        payer_id: required('Please select a valid Payer.'),
      };
    },
    schemaRules() {
      const rules = {};
      if (!this.schema) {
        return rules;
      }

      const combo = (this.combos && this.combos[this.form.selectedCombo]) || [];
      for (const key of combo) {
        if (!serverValidatedComboFields.includes(key)) {
          continue;
        }
        const field = this.schema[key];
        if (field && field.allowed) {
          rules[key] = required(`${field.label} is required.`);
        }
      }

      // Nothing beyond this: the client must not reject what the server accepts.
      if (this.schema.serviceType && this.schema.serviceType.required) {
        rules.serviceType = requiredAny('Please select one or more service types.');
      }

      return rules;
    },
    validationRules() {
      return Object.assign({}, this.referenceRules, this.schemaRules);
    },
    // comboOptions renders each accepted field combination with the payer's own
    // labels (e.g. "Patient ID + Date of Birth") instead of raw field names.
    comboOptions() {
      return (this.combos || []).map((fields, index) => ({
        index,
        label: fields.map((name) => this.schema?.[name]?.label || name).join(' + '),
      }));
    },
    comboHelp() {
      const count = this.comboOptions.length;
      return `This payer accepts ${count} combinations. Pick the one matching your details.`;
    },
    serviceType: {
      get() {
        const multiple = this.schema?.serviceType?.multiple;
        const isArray = Array.isArray(this.form.serviceType);
        if (multiple && !isArray) {
          return [this.form.serviceType];
        }
        return this.form.serviceType;
      },
      set(value) {
        this.form.serviceType = value;
      },
    },
    draftId: {
      get() {
        if (this.inquiry && this.inquiry.status === 'draft') {
          return this.inquiry.id;
        }
        return null;
      },
      set(value) {
        this.form.draftId = value;
      },
    },
    parentId: {
      get() {
        if (this.inquiry && this.inquiry.id !== '') {
          return this.inquiry.id;
        }
        return null;
      },
      set(value) {
        this.form.parentId = value;
      },
    },
  },
  methods: {
    cleanForm() {
      this.schema = null;
      this.combos = null;
      this.form.selectedCombo = 0;
      this.form.errors.clear();

      for (let field in this.form.data()) {
        if (!copyableFormFields.includes(field)) {
          this.form[field] = null;
        }
      }
    },
    updateSchema(payer_id) {
      this.loading = true;
      let url = `/payers/${payer_id}/schema`;
      request({ url: url })
        .then((resp) => {
          this.schema = applyShortLabels(resp.data.schema.fields);
          this.combos = resp.data.schema.combos;
          this.loading = resp.data.loading;
        })
        .catch(() => {
          this.schema = null;
          this.combos = null;
          this.form.errors.errors.NOFIELD = 'The payer is having issues.';
          this.loading = false;
        })
        .finally(() => {
          if (!this.loading) {
            this.loading = false;
          }
        });
    },
    handlePayerSelected(payer_id) {
      this.cleanForm();
      this.form.payer_id = payer_id;
      if (payer_id == null) {
        return;
      }
      this.updateSchema(payer_id);
    },
    // populateForm fills in form fields with params. Used for InquiryCopy.
    populateForm(inquiry) {
      // First try to get data from inquiry level
      if (inquiry.patient_first_name) {
        this.$set(this.form, 'patientFirstName', inquiry.patient_first_name);
      }
      if (this.inquiry.patient_last_name) {
        this.$set(this.form, 'patientLastName', this.inquiry.patient_last_name);
      }
      // Then try to get data from coverage
      if (inquiry?.coverage) {
        if (inquiry.coverage.patient) {
          if (inquiry.coverage.patient.dob) {
            // Parse the ISO string directly into UTC components
            const [datePart] = inquiry.coverage.patient.dob.split('T');
            const [year, month, day] = datePart.split('-').map(Number);

            // Create UTC date at noon
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            this.$set(this.form, 'patientBirthDate', utcDate);
          }
          if (!this.form.subscriberRelationship && this.inquiry.coverage.patient.relationship) {
            // Map relationship display name to code
            const relationshipMap = {
              Self: '18',
              Spouse: '01',
              Child: '19',
              Employee: '20',
            };
            const relationshipCode =
              relationshipMap[this.inquiry.coverage.patient.relationship] ||
              this.inquiry.coverage.patient.relationship;
            this.$set(this.form, 'subscriberRelationship', relationshipCode);
          }
        }
        // Check subscriber data
        if (this.inquiry.coverage.subscriber) {
          if (!this.form.memberId && this.inquiry.coverage.subscriber.member_id) {
            this.$set(this.form, 'memberId', this.inquiry.coverage.subscriber.member_id);
          }
        }
      }

      // Finally, use submission data if available and not already set
      for (const param in inquiry.submission) {
        // Never prepopulate the asOfDate
        if (param === 'asOfDate') {
          continue;
        } else if (
          param === 'patientBirthDate' &&
          !this.form.patientBirthDate &&
          inquiry.submission[param] != null
        ) {
          // Parse the ISO string directly into UTC components
          const [datePart] = inquiry.submission[param].split('T');
          const [year, month, day] = datePart.split('-').map(Number);
          // Create UTC date at noon
          const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
          this.$set(this.form, param, utcDate);
        } else if (!this.form[param]) {
          this.$set(this.form, param, inquiry.submission[param]);
        }
      }

      if (!this.providerOptions.find(({ id }) => id === this.form.provider_id)) {
        this.form.provider_id = null;
        this.form.errors.errors.provider_id =
          'The provider used is no longer available, please select a different one.';
      }

      if (!this.payerOptions.find(({ id }) => id === this.form.payer_id)) {
        // If payer_id doesn't match any top-level payer, check children
        let parentFound = false;
        for (const parentPayer of this.payerOptions) {
          if (parentPayer.children && Array.isArray(parentPayer.children)) {
            // Check if payer_id exists in any child
            const childMatch = parentPayer.children.find(
              (child) => child.id === this.form.payer_id
            );
            if (childMatch) {
              // Found matching child, select the parent instead
              this.form.payer_id = parentPayer.id;
              parentFound = true;
              break;
            }
          }
        }

        // If no parent found for the child, then show error
        if (!parentFound) {
          this.form.payer_id = null;
          this.form.errors.errors.payer_id =
            'The payer used is no longer available, please select a different one.';
        }
      }

      if (this.form.payer_id) {
        this.updateSchema(this.form.payer_id);
      }

      //add draftId to the form data if it exists
      // this means that this is a submit after draft and we should remove the draft
      this.form.draftId = this.draftId;
      // add parentId to form data if it exists
      // this means we are resubmitting an inquiry
      this.form.parentId = this.parentId;
    },
    validateForm(rules) {
      this.form.errors.clear();

      // Buefy's checkbox group leaves holes behind.
      if (Array.isArray(this.form.serviceType)) {
        this.form.serviceType = this.form.serviceType.filter((el) => el != null);
      }

      const errors = validate(this.form.data(), rules);
      if (Object.keys(errors).length === 0) {
        return true;
      }
      this.form.errors.merge(errors);
      this.form.errors.setFormError(fieldSummaryMessage);
      this.revealErroredFields();
      return false;
    },
    // Reveal an errored field the current combo hides, so it can be corrected.
    revealErroredFields() {
      const combo = this.combos && this.combos[this.form.selectedCombo];
      if (!combo) {
        return;
      }
      for (const key of this.form.errors.fieldKeys()) {
        if (!comboFields.includes(key) || !this.schema || !this.schema[key]) {
          continue;
        }
        if (!combo.includes(key)) {
          combo.push(key);
        }
      }
    },
    handleSubmitErrors(err) {
      const errors = (err && err.errors) || {};
      const fields = {};
      for (const key of Object.keys(errors)) {
        if (!vendorErrorKeys.includes(key) && key !== 'NOFIELD') {
          fields[key] = errors[key];
        }
      }
      this.form.errors.merge(fields);

      const vendorMessage = errors.Message || errors.ErrorDescription;
      if (vendorMessage) {
        this.form.errors.setFormError(vendorMessage);
      } else if (this.form.errors.hasFields()) {
        this.form.errors.setFormError(fieldSummaryMessage);
      } else {
        this.form.errors.setGenericError();
      }
      this.revealErroredFields();
      this.loading = false;
    },
    handleSubmit(action) {
      // Both buttons are native-type="submit", so this also fires with the
      // submit event itself; neither branch matches it.
      if (action == 'draft') {
        // A draft is deliberately incomplete: provider and payer only.
        if (!this.validateForm(this.referenceRules)) {
          return;
        }
        this.loading = true;
        if (this.draftId) {
          this.updateInquiryDraft();
        } else {
          this.createInquiryDraft();
        }
      } else if (action == 'submit') {
        if (!this.validateForm(this.validationRules)) {
          return;
        }
        this.loading = true;
        this.createInquiry();
      }
    },
    createInquiry() {
      this.form
        .post('/inquiries')
        .then((resp) => {
          let inq = resp.inquiry;
          this.handleInquiryResponse(inq);
        })
        .catch((err) => {
          this.handleSubmitErrors(err);
        });
    },
    createInquiryDraft() {
      this.form
        .post('/inquiries/draft')
        .then((resp) => {
          let inq = resp.inquiry;
          this.handleInquiryResponse(inq);
        })
        .catch((err) => {
          this.handleSubmitErrors(err);
        });
    },
    updateInquiryDraft() {
      this.form
        .patch(`/inquiries/${this.draftId}/draft`)
        .then((resp) => {
          let inq = resp.inquiry;
          this.handleInquiryResponse(inq);
        })
        .catch((err) => {
          this.handleSubmitErrors(err);
        });
    },
    handleInquiryResponse(inq) {
      // if the inquiry is not complete, we need to set the parentId
      // so we can use it as a parent for the next retry.
      if (inq.status !== 'complete' && inq.id !== '') {
        this.parentId = inq.id;
      }
      if (inq.status === 'complete') {
        this.$router.push({
          name: 'InquiryDetail',
          params: { id: inq.id },
        });
        // This early return keeps loading from being set to false before we transition.
        return;
      } else if (inq.status === 'draft') {
        this.$router.push({ name: 'InquiryList' });
        return;
      } else if (Object.keys(inq.remote_errors).length != 0) {
        for (let key in inq.remote_errors) {
          if (key.startsWith('provider')) {
            this.form.errors.record({
              NOFIELD: `There seems to be a problem with your configured provider information. (${key})`,
            });
          } else if (key.startsWith('payer')) {
            this.form.errors.record({
              NOFIELD: `There seems to be a problem with this payer. (${key})`,
            });
          } else {
            this.form.errors.record({
              NOFIELD: `An unexpected error occurred for ${key}: ${inq.remote_errors[key]}`,
            });
          }
        }
      } else if (inq.status === 'max-retry') {
        this.$buefy.toast.open({
          message: 'The payer is taking a long time to respond. Please try again later.',
          type: 'is-warning',
          duration: 20000,
        });
        this.$router.push({ name: 'InquiryList' });
      } else if (inq.status === 'pending') {
        this.$buefy.toast.open({
          message: 'The payer is having issues. Please try again later.',
          type: 'is-danger',
          duration: 20000,
        });
        this.$router.push({ name: 'InquiryList' });
      } else if (inq.status === 'error') {
        this.form.errors.setGenericError();
      } else {
        this.form.errors.setGenericError();
      }
      // This is here instead of in finally() so we can short
      // circut, to keep 'loading' until we transition fully.
      this.loading = false;
    },
    dateParser: dateParser,
  },
};
</script>

<style scoped>
.save-draft-btn {
  background-color: #d3d3d3;
  color: #333;
}

/* Form footer layout */
.form-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
}

.footer-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.datepicker-wrapper {
  min-width: 200px;
}

.submit-buttons-row {
  margin-left: auto;
}

/* As Of Today button style - matches medium button size */
.as-of-today-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 48px;
  box-sizing: border-box;
  font-size: 1.25rem;
}

.as-of-today-btn:hover {
  background-color: #fafafa;
  border-color: #b5b5b5;
}

.as-of-today-btn.is-active {
  background-color: #f8f8f8;
  border-color: #b5b5b5;
}

.as-of-today-btn .b-switch {
  margin: 0 !important;
}

.as-of-today-text {
  font-size: 1.25rem;
  font-weight: 400;
  color: #363636;
  white-space: nowrap;
}

@media (max-width: 1024px) {
  .form-footer {
    flex-direction: column;
    width: 100%;
  }

  .footer-row {
    width: 100%;
    display: flex;
    gap: 0.5rem;
  }

  .as-of-today-wrapper {
    flex: 1;
  }

  .as-of-today-btn {
    width: 100%;
    height: 48px;
    padding: 0.75rem 1rem;
  }

  .datepicker-wrapper {
    flex: 1;
    min-width: 0;
  }

  .submit-buttons-row {
    width: 100%;
    margin-left: 0;
    margin-top: 0.5rem;
  }

  .submit-buttons {
    display: flex;
    width: 100%;
    gap: 0.5rem;
    flex-wrap: nowrap;
  }

  .submit-buttons .submit-btn {
    flex: 1 1 0;
    min-width: 0;
    margin: 0 !important;
  }
}
</style>
